import BuyerSalesExplorer from "./BuyerSalesExplorer";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api } from "../lib/api";
import { CHART_COLORS, formatNum, compact } from "../lib/format";
import { formatDateValue, formatReportValue, isReportDateColumn } from "../lib/dateFormat";
import { downloadCsv } from "../lib/csv";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, Treemap,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from "recharts";
import { AlertCircle, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

function chartPngBlob(svg, title, filters) {
  const bounds = svg.getBoundingClientRect();
  const width = Math.max(1, Math.ceil(bounds.width));
  const chartHeight = Math.max(1, Math.ceil(bounds.height));
  const filterText = (filters || []).map((filter) => {
    const value = filter?.value;
    const printable = value && typeof value === "object"
      ? `${formatDateValue(value.from) || "…"} to ${formatDateValue(value.to) || "…"}`
      : Array.isArray(value) ? value.join(", ") : String(value ?? "");
    return printable ? `${filter.field}: ${printable}` : "";
  }).filter(Boolean);
  const subtitle = filterText.join(" · ") || "All filters";
  const headerHeight = 54;
  const height = chartHeight + headerHeight;
  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(chartHeight));

  const originalNodes = [svg, ...svg.querySelectorAll("*")];
  const clonedNodes = [clone, ...clone.querySelectorAll("*")];
  originalNodes.forEach((node, index) => {
    const computed = window.getComputedStyle(node);
    const inlineStyle = Array.from(computed)
      .map((property) => `${property}:${computed.getPropertyValue(property)}`)
      .join(";");
    if (inlineStyle) clonedNodes[index].setAttribute("style", inlineStyle);
  });

  const source = new Blob(
    [new XMLSerializer().serializeToString(clone)],
    { type: "image/svg+xml;charset=utf-8" }
  );
  const imageUrl = URL.createObjectURL(source);

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(imageUrl);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Unable to prepare the chart image."));
        return;
      }
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      context.fillStyle = "#111827";
      context.font = "600 16px system-ui, sans-serif";
      context.fillText(title || "Chart", 16, 23, width - 32);
      context.fillStyle = "#4b5563";
      context.font = "12px system-ui, sans-serif";
      const words = subtitle.split(/\s+/);
      let line = "";
      let y = 43;
      words.forEach((word) => {
        const candidate = line ? `${line} ${word}` : word;
        if (line && context.measureText(candidate).width > width - 32 && y < headerHeight - 4) {
          context.fillText(line, 16, y, width - 32);
          line = word;
          y += 14;
        } else {
          line = candidate;
        }
      });
      if (line) context.fillText(line, 16, y, width - 32);
      context.drawImage(image, 0, headerHeight, width, chartHeight);
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Unable to create the chart image."));
      }, "image/png");
    };
    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error("Unable to render the chart image."));
    };
    image.src = imageUrl;
  });
}

function EmptyState({ msg = "No data" }) {
  return (
    <div className="h-full flex items-center justify-center text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
      {msg}
    </div>
  );
}

export function WidgetLoadingState() {
  return (
    <div
      role="status"
      aria-label="Loading widget data"
      className="h-full flex items-center justify-center"
      style={{ color: "var(--c-text-muted)" }}
    >
      <Loader2 size={18} className="animate-spin" strokeWidth={1.7} />
    </div>
  );
}

function ErrorState({ msg, onRetry }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-1.5 text-center px-3" data-testid="widget-error">
      <AlertCircle size={16} strokeWidth={1.7} color="#fa4d56" />
      <div className="text-[12px] leading-tight" style={{ color: "#991b1b" }}>{msg}</div>
      {onRetry && (
        <button className="btn btn-ghost text-[11px] mt-1" onClick={onRetry} data-testid="widget-retry">Retry</button>
      )}
    </div>
  );
}

function Kpi({ data, fmt, title, onDrillDown, exactOnly = false }) {
  const value = data?.value ?? 0;
  const exact = formatNum(value, fmt || {});
  const content = (
    <>
      <div className="kpi-value" title={exact}>{exactOnly ? exact : compact(value)}</div>
      {!exactOnly && (
        <div className="text-[11px] mt-1 numeric" style={{ color: "var(--c-text-muted)" }}>
          {exact}
        </div>
      )}
      {null}
    </>
  );

  if (!onDrillDown) {
    return (
      <div className="h-full flex flex-col justify-center px-1" data-testid={`kpi-${title || "widget"}`}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="h-full w-full flex flex-col justify-center px-1 text-left rounded-md"
      onClick={() => onDrillDown({ label: title || "Total", value })}
      data-testid={`kpi-${title || "widget"}`}
      title="View detailed rows"
      style={{ cursor: "pointer" }}
    >
      {content}
    </button>
  );
}

function FarmAuditSummary({ data, onDrillDown }) {
  const summary = data?.summary || {};
  const metrics = [
    ["Total Farms", summary.total_farms],
    ["BAP", summary.bap_farms],
    ["ASC", summary.asc_farms],
    ["Non-Certified", summary.non_certified_farms],
  ];

  const secondary = [
    ["Non-BAP", summary.non_bap_farms],
    ["Unmarked", summary.unmarked_farms],
  ];

  return (
    <div className="h-full flex flex-col justify-center px-2 py-2" data-testid="farm-audit-summary">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {metrics.map(([label, value]) => (
          <button
            key={label}
            type="button"
            className="text-left rounded-md border px-2.5 py-2"
            style={{ borderColor: "var(--c-border)", background: "var(--c-surface)" }}
            onClick={() => onDrillDown?.({ label, value: Number(value || 0) })}
          >
            <div className="text-[11px]" style={{ color: "var(--c-text-muted)" }}>{label}</div>
            <div className="kpi-value mt-0.5">{Number(value || 0).toLocaleString()}</div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-4 mt-2 px-1 text-[10.5px]" style={{ color: "var(--c-text-muted)" }}>
        {secondary.map(([label, value]) => (
          <span key={label}>
            {label}: <span className="numeric">{Number(value || 0).toLocaleString()}</span>
          </span>
        ))}
        {data?.categories_are_non_exclusive && (
          <span>Certification categories are non-exclusive</span>
        )}
      </div>
    </div>
  );
}

function _cleanRows(rows) {
  // Group rows whose label is blank/null/'-' under "Unassigned"
  const out = [];
  let unassignedTotal = 0;
  let unassignedCount = 0;
  for (const r of rows || []) {
    const lbl = r.label;
    const isBlank = lbl == null || String(lbl).trim() === "" || lbl === "—" || String(lbl).trim() === "-";
    if (isBlank) {
      unassignedTotal += Number(r.value || 0);
      unassignedCount += 1;
    } else {
      out.push({ ...r, label: String(lbl) });
    }
  }
  if (unassignedCount > 0) out.push({ label: "Unassigned", value: unassignedTotal });
  return out;
}

function TooltipBox({ active, payload, label, fmt, total }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded shadow-sm px-2.5 py-1.5 text-[11.5px]" style={{ borderColor: "var(--c-border)" }}>
      <div className="font-medium text-gray-700">{formatDateValue(label ?? payload[0]?.name)}</div>
      {payload.map((p, i) => {
        const v = Number(p.value || 0);
        const pct = total ? ((v / total) * 100).toFixed(1) + "%" : null;
        return (
          <div key={i} className="flex items-center gap-1.5 numeric">
            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: p.color || p.payload?.fill }} />
            <span style={{ color: "var(--c-text)" }}>{formatNum(v, fmt)}{pct ? ` · ${pct}` : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

function BarW({ data, dimension, fmt, axisLabels, onDrillDown, buyerSales = false, widget }) {
  const isCollectionRateByInvoice = widget?.id === "ceo-commercial-collection-rate";
  const rows = _cleanRows(data?.rows).filter((r) => !(widget?.exclude_unassigned && r.label === "Unassigned"));
  const valueAxis = axisLabels?.y || fieldLabel(widget?.measure);
  const categoryAxis = axisLabels?.x || fieldLabel(dimension || widget?.dimension);
  if (buyerSales) return <BuyerSalesExplorer rows={rows} capped={data?.truncated === true} categoryLimit={data?.category_limit} fmt={fmt} onDrillDown={onDrillDown} />;
  if (!rows.length) return <EmptyState />;

  // All bar charts render vertically; long category names (buyers,
  // suppliers, varieties) get a taller, angled axis to stay readable.
  const longLabels = rows.some((r) => String(r.label ?? "").length > 12);

  // More categories than fit: the chart grows to ~44px per bar and the card
  // scrolls horizontally, so no category is dropped and labels stay readable.
  const perBar = 44;
  const minWidth = rows.length * perBar + 60;
  const many = rows.length > 10;
  const tickLabel = (v) => {
    const s = String(v ?? "");
    const max = longLabels ? 24 : 16;
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
  };

  const purchaseStationTickLabel = (v) => {
    const s = String(v ?? "").trim();
    const match = s.match(/\(([^()]*)\)\s*$/);
    return match ? match[1].trim() : tickLabel(v);
  };

  const commercialBuyerTickLabel = (v) => {
    const s = String(v ?? "").trim();
    const match = s.match(/\(([^()]*)\)\s*$/);
    return match ? match[1].trim() : tickLabel(v);
  };
  return (
    <div style={{ width: "100%" }}>
    <AxisCaption x={categoryAxis} y={valueAxis} />
    <div style={{ width: "100%", height: 278, overflowX: "auto", overflowY: "hidden" }}>
     <div style={{ width: "100%", minWidth: many ? minWidth : undefined, height: "100%" }}>
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 280 }}>
        <BarChart
          data={rows}
          margin={{ top: 6, right: 8, left: 0, bottom: 6 }}
          onClick={(state) => {
            if (!onDrillDown) return;
            const index = Number(state?.activeTooltipIndex ?? state?.activeIndex);
            const point = Number.isInteger(index) && rows[index]
              ? rows[index]
              : rows.find((row) => String(row.label) === String(state?.activeLabel));
            if (point) onDrillDown(point);
          }}
          style={{ cursor: onDrillDown ? "pointer" : "default" }}
        >
          <CartesianGrid
            strokeDasharray="2 3"
            stroke="#eef2f7"
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10.5, fill: "#6b7280" }}
            interval={0}
            tickFormatter={
              isCollectionRateByInvoice
                ? (v) => String(v ?? "")
                : widget?.id === "ceo-purchase-station-weight"
                  ? purchaseStationTickLabel
                  : widget?.id === "ceo-commercial-buyer-sales"
                    ? commercialBuyerTickLabel
                    : tickLabel
            }
            angle={rows.length > 6 || longLabels ? -35 : 0}
            textAnchor={rows.length > 6 || longLabels ? "end" : "middle"}
            height={longLabels ? 110 : rows.length > 6 ? 70 : 22}
          />
          <YAxis
            tick={{ fontSize: 10.5, fill: "#6b7280" }}
            tickFormatter={compact}
            width={56}
            domain={[0, "auto"]}
          />
          <Tooltip
            content={<TooltipBox fmt={fmt} />}
            cursor={{ fill: "rgba(15,98,254,0.06)" }}
          />
          <Bar
            dataKey="value"
            fill={CHART_COLORS[0]}
            radius={[2, 2, 0, 0]}
            cursor={onDrillDown ? "pointer" : "default"}
          />
        </BarChart>
      </ResponsiveContainer>
     </div>
    </div>
    </div>
  );
}


function TreemapW({
  data,
  fmt,
  onDrillDown,
  excludeUnassigned = false,
  entityLabel = "Item",
}) {
  const cleaned = _cleanRows(data?.rows);
  if (!cleaned.length) return <EmptyState />;

  const unassigned = cleaned.find(
    (row) => String(row.label || "").trim().toLowerCase() === "unassigned"
  );

  let rows = cleaned.filter((row) => {
    if (!excludeUnassigned) return true;
    return String(row.label || "").trim().toLowerCase() !== "unassigned";
  });

  rows = rows
    .filter((row) => Number(row.value || 0) > 0)
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));

  if (!rows.length) {
    return <EmptyState msg={`No mapped ${entityLabel.toLowerCase()} data`} />;
  }

  const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);

  const chartRows = rows.map((row, index) => ({
    ...row,
    name: row.label,
    size: Number(row.value || 0),
    _index: index,
  }));

  const TreemapCell = ({
    x,
    y,
    width,
    height,
    index,
  }) => {
    const row = chartRows[index];
    if (!row || width <= 0 || height <= 0) return null;

    const value = Number(row.value || 0);
    const pct = total ? (value / total) * 100 : 0;

    const showName = width >= 72 && height >= 30;
    const showValue = width >= 105 && height >= 54;
    const showPct = width >= 125 && height >= 72;

    const TREEMAP_BLUES = [
      "#315A8C",
      "#3F6F9F",
      "#527FAA",
      "#668FB3",
      "#7A9FBC",
      "#91AFC6",
      "#A8BED0",
      "#BECEDB",
    ];

    const fill =
      TREEMAP_BLUES[
        Math.min(
          TREEMAP_BLUES.length - 1,
          Math.floor((index / Math.max(chartRows.length - 1, 1)) * TREEMAP_BLUES.length)
        )
      ];

    return (
      <g
        onClick={() => {
          if (onDrillDown) onDrillDown(row);
        }}
        style={{ cursor: onDrillDown ? "pointer" : "default" }}
      >
        <rect
          x={x + 1}
          y={y + 1}
          width={Math.max(width - 2, 0)}
          height={Math.max(height - 2, 0)}
          rx={4}
          ry={4}
          fill={fill}
          stroke="#ffffff"
          strokeWidth={2}
        />

        {showName && (
          <text
            x={x + 9}
            y={y + 19}
            fill="#ffffff"
            fontSize={11}
            fontWeight={600}
          >
            {String(row.label).length > 30
              ? `${String(row.label).slice(0, 28)}…`
              : row.label}
          </text>
        )}

        {showValue && (
          <text
            x={x + 9}
            y={y + 36}
            fill="rgba(255,255,255,0.92)"
            fontSize={10.5}
          >
            {compact(value)}
          </text>
        )}

        {showPct && (
          <text
            x={x + 9}
            y={y + 51}
            fill="rgba(255,255,255,0.82)"
            fontSize={9.5}
          >
            {pct.toFixed(1)}%
          </text>
        )}
      </g>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 14,
          marginBottom: 10,
          padding: "9px 11px",
          border: "1px solid #dbe4ee",
          borderRadius: 7,
          background: "#f8fafc",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10.5,
              color: "#64748b",
              marginBottom: 2,
            }}
          >
            Mapped {entityLabel}s
          </div>
          <div
            style={{
              fontSize: 18,
              lineHeight: 1.1,
              fontWeight: 650,
              color: "#163A63",
            }}
          >
            {chartRows.length}
          </div>
        </div>

        <div
          style={{
            fontSize: 10.5,
            color: "#64748b",
            textAlign: "right",
            maxWidth: 260,
          }}
        >
          Larger tiles represent higher contribution.
          Hover for the full client name and value.
        </div>
      </div>

      {excludeUnassigned && unassigned && Number(unassigned.value || 0) > 0 && (
        <div
          style={{
            marginBottom: 10,
            padding: "9px 11px",
            border: "1px solid #dbe4f0",
            borderRadius: 7,
            background: "#fbfcfe",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            fontSize: 11.5,
          }}
        >
          <span style={{ color: "#475569" }}>
            Sales value with no {String(entityLabel).toLowerCase()} linked
          </span>
          <strong style={{ color: "#0f172a" }}>
            {formatNum(Number(unassigned.value || 0), fmt)}
          </strong>
        </div>
      )}

      <div style={{ width: "100%", height: 390, minHeight: 390 }}>
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 280 }}>
          <Treemap
            data={chartRows}
            dataKey="size"
            nameKey="name"
            aspectRatio={4 / 3}
            stroke="#ffffff"
            content={<TreemapCell />}
          >
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;

                const raw =
                  payload[0]?.payload?.payload ||
                  payload[0]?.payload ||
                  {};

                const label =
                  raw.label ||
                  raw.name ||
                  payload[0]?.name ||
                  entityLabel;

                const value = Number(
                  raw.value ??
                  raw.size ??
                  payload[0]?.value ??
                  0
                );

                const pct = total
                  ? ((value / total) * 100).toFixed(2)
                  : "0.00";

                return (
                  <div
                    className="bg-white border rounded shadow-sm px-3 py-2 text-[11.5px]"
                    style={{ borderColor: "var(--c-border)" }}
                  >
                    <div className="font-medium text-gray-700 mb-1">
                      {label}
                    </div>
                    <div className="numeric" style={{ color: "var(--c-text)" }}>
                      {formatNum(value, fmt)}
                    </div>
                    <div style={{ color: "var(--c-text-muted)" }}>
                      {pct}% of mapped contribution
                    </div>
                  </div>
                );
              }}
            />
          </Treemap>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          marginTop: 7,
          fontSize: 10.5,
          color: "var(--c-text-muted)",
        }}
      >
        Click any tile to open detailed rows.
      </div>
    </div>
  );
}


function ContributionListW({
  data,
  fmt,
  onDrillDown,
  excludeUnassigned = false,
  entityLabel = "Client",
  coverage = null,
  includeNonPositive = false,
}) {
  const cleaned = _cleanRows(data?.rows);
  if (!cleaned.length) return <EmptyState />;

  const unassigned = cleaned.find(
    (row) =>
      String(row.label || "").trim().toLowerCase() === "unassigned"
  );

  let rows = cleaned.filter((row) => {
    if (!excludeUnassigned) return true;
    return String(row.label || "").trim().toLowerCase() !== "unassigned";
  });

  rows = rows
    .filter((row) => includeNonPositive || Number(row.value || 0) > 0)
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));

  if (!rows.length) {
    return <EmptyState msg={`No mapped ${entityLabel.toLowerCase()} data`} />;
  }

  const total = rows.reduce(
    (sum, row) => sum + Number(row.value || 0),
    0
  );

  const maxValue = Math.max(
    ...rows.map((row) => Math.abs(Number(row.value || 0))),
    1
  );

  const masterTotal = Number(coverage?.master_total || 0);
  const linkedForCoverage = includeNonPositive
    ? rows.filter((row) => String(row.label).toLowerCase() !== "unassigned").length
    : rows.length;

  const coverageLabel =
    masterTotal > 0
      ? `${linkedForCoverage} of ${masterTotal}`
      : String(rows.length);

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          gap: 14,
          marginBottom: 10,
          padding: "10px 12px",
          border: "1px solid #dfe6ee",
          borderRadius: 7,
          background: "#fafbfd",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10.5,
              color: "#64748b",
              marginBottom: 2,
            }}
          >
            {entityLabel}s with Sales
          </div>

          <div
            style={{
              fontSize: 19,
              lineHeight: 1.1,
              fontWeight: 650,
              color: "#173b63",
            }}
          >
            {coverageLabel}
          </div>

          {masterTotal > 0 && (
            <div
              style={{
                marginTop: 3,
                fontSize: 10,
                color: "#94a3b8",
                lineHeight: 1.35,
              }}
            >
              {masterTotal} ERP master records{coverage?.active_master_total != null ? ` (${coverage.active_master_total} active${coverage.inactive_master_total ? `, ${coverage.inactive_master_total} inactive` : ""})` : ""} | {linkedForCoverage} distinct Sales names in the current selection
            </div>
          )}
        </div>

        <div
          style={{
            fontSize: 10.5,
            lineHeight: 1.4,
            color: "#64748b",
            textAlign: "right",
          }}
        >
          Ranked by contribution
          <br />
          Click a client for detailed rows
        </div>
      </div>

      {excludeUnassigned &&
        unassigned &&
        Number(unassigned.value || 0) > 0 && (
          <div
            style={{
              marginBottom: 10,
              padding: "8px 11px",
              border: "1px solid #e2e8f0",
              borderRadius: 7,
              background: "#fcfcfd",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              fontSize: 11.5,
            }}
          >
            <span style={{ color: "#64748b" }}>
              Sales value with no {String(entityLabel).toLowerCase()} linked
            </span>

            <strong style={{ color: "#334155" }}>
              {formatNum(Number(unassigned.value || 0), fmt)}
            </strong>
          </div>
        )}

      <div
        style={{
          border: "1px solid #e3e8ef",
          borderRadius: 7,
          overflow: "hidden",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "42px minmax(240px, 1fr) 125px 80px 150px",
            gap: 10,
            alignItems: "center",
            padding: "8px 12px",
            background: "#f4f7fa",
            borderBottom: "1px solid #dfe6ee",
            fontSize: 10.5,
            fontWeight: 600,
            color: "#526173",
          }}
        >
          <div>Rank</div>
          <div>{entityLabel}</div>
          <div style={{ textAlign: "right" }}>Sales Value</div>
          <div style={{ textAlign: "right" }}>Share</div>
          <div>Contribution</div>
        </div>

        <div
          style={{
            height: 520,
            minHeight: 520,
            maxHeight: 520,
            overflowY: "auto",
          }}
        >
          {rows.map((row, index) => {
            const value = Number(row.value || 0);
            const pct = total ? (value / total) * 100 : 0;
            const relative = (Math.abs(value) / maxValue) * 100;

            return (
              <button
                key={`${row.label}-${index}`}
                type="button"
                onClick={() => {
                  if (onDrillDown) onDrillDown(row);
                }}
                style={{
                  width: "100%",
                  border: 0,
                  borderBottom:
                    index === rows.length - 1
                      ? "none"
                      : "1px solid #edf1f5",
                  background: index % 2 === 0 ? "#ffffff" : "#fbfcfd",
                  display: "grid",
                  gridTemplateColumns:
                    "42px minmax(240px, 1fr) 125px 80px 150px",
                  gap: 10,
                  alignItems: "center",
                  padding: "9px 12px",
                  textAlign: "left",
                  cursor: onDrillDown ? "pointer" : "default",
                }}
                title={`Open ${row.label} details`}
              >
                <div
                  style={{
                    fontSize: 10.5,
                    color: "#94a3b8",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {index + 1}
                </div>

                <div
                  style={{
                    fontSize: 11.5,
                    color: "#334155",
                    fontWeight: index < 5 ? 600 : 500,
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    lineHeight: 1.3,
                  }}
                >
                  {row.label}
                </div>

                <div
                  style={{
                    textAlign: "right",
                    fontSize: 11.5,
                    color: "#1f2937",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatNum(value, fmt)}
                </div>

                <div
                  style={{
                    textAlign: "right",
                    fontSize: 11,
                    color: "#64748b",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {pct.toFixed(2)}%
                </div>

                <div
                  style={{
                    height: 7,
                    background: "#eef3f7",
                    borderRadius: 999,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.max(relative, 1.5)}%`,
                      height: "100%",
                      borderRadius: 999,
                      background:
                        index === 0
                          ? "#557da8"
                          : index < 5
                            ? "#7f9fbe"
                            : "#abc0d3",
                    }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// "LineValueUSD" -> "Line Value USD"
export function fieldLabel(field) {
  const value = String(field || "");
  if (value === "PriceNative") return "Rate";
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\bUsd\b/gi, "USD").replace(/\bKgs\b/gi, "KGS").replace(/\bMcs\b/gi, "MCS")
    .trim();
}

function AxisCaption({ x, y }) {
  if (!x && !y) return null;
  return (
    <div className="flex justify-between gap-3 mb-1 text-[11px]" style={{ color: "#64748b" }}>
      <span>{y ? `Y-axis: ${y}` : ""}</span>
      <span>{x ? `X-axis: ${x}` : ""}</span>
    </div>
  );
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Daily points over a long span become an unreadable, gappy axis. Beyond ~3
// months the (summed) values roll up into calendar months; each point keeps
// its date range so a click drills into exactly that period.
function toTimeSeries(rows) {
  const dated = rows.filter((r) => ISO_DATE.test(String(r.label)));
  if (dated.length !== rows.length || !rows.length) {
    return { grain: null, points: [...rows].sort((a, b) => String(a.label).localeCompare(String(b.label))) };
  }
  const sorted = [...rows].sort((a, b) => String(a.label).localeCompare(String(b.label)));
  const first = new Date(String(sorted[0].label).slice(0, 10));
  const last = new Date(String(sorted[sorted.length - 1].label).slice(0, 10));
  const spanDays = (last - first) / 86400000;
  if (spanDays <= 92) {
    return {
      grain: "day",
      points: sorted.map((r) => {
        const d = String(r.label).slice(0, 10);
        return { ...r, label: d, range: { from: d, to: d } };
      }),
    };
  }
  const byMonth = new Map();
  for (const r of sorted) {
    const key = String(r.label).slice(0, 7);
    byMonth.set(key, (byMonth.get(key) || 0) + Number(r.value || 0));
  }
  return {
    grain: "month",
    points: [...byMonth.entries()].map(([key, value]) => {
      const [y, m] = key.split("-").map(Number);
      const lastDay = new Date(y, m, 0).getDate();
      return {
        label: `${MONTHS[m - 1]} ${y}`,
        value,
        range: { from: `${key}-01`, to: `${key}-${String(lastDay).padStart(2, "0")}` },
      };
    }),
  };
}

function LineW({ data, fmt, onDrillDown, widget }) {
  if (!data?.rows?.length) return <EmptyState />;
  const { grain, points: formatted } = toTimeSeries(data.rows);
  const lineMinWidth = formatted.length * 28 + 60;
  const manyPoints = formatted.length > 24;
  const measure = fieldLabel(widget?.measure);
  const xCaption = grain === "month"
    ? `Month (${fieldLabel(widget?.dimension)}) — monthly totals`
    : grain === "day"
      ? `Day (${fieldLabel(widget?.dimension)}) — daily totals`
      : fieldLabel(widget?.dimension);
  // Recharts 3 passes activeIndex/activeLabel (no activePayload) to chart
  // clicks, so resolve the clicked point from our own data.
  const pointFromState = (state) => {
    const idx = Number(state?.activeIndex ?? state?.activeTooltipIndex);
    if (Number.isInteger(idx) && formatted[idx]) return formatted[idx];
    return formatted.find((p) => p.label === state?.activeLabel) || null;
  };
  return (
    <div style={{ width: "100%", height: 300, minHeight: 300 }}>
     <AxisCaption x={xCaption} y={measure} />
     <div style={{ width: "100%", height: 276, overflowX: "auto", overflowY: "hidden" }}>
     <div style={{ width: "100%", minWidth: manyPoints ? lineMinWidth : undefined, height: "100%" }}>
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 276 }}>
        <LineChart
          data={formatted}
          margin={{ top: 6, right: 12, left: 4, bottom: 6 }}
          onClick={(state) => {
            const point = pointFromState(state);
            if (onDrillDown && point) onDrillDown(point);
          }}
          style={{ cursor: onDrillDown ? "pointer" : "default" }}
        >
          <CartesianGrid strokeDasharray="2 3" stroke="#eef2f7" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6b7280" }} minTickGap={24} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 10.5, fill: "#6b7280" }} tickFormatter={compact} width={56} domain={[0, "auto"]} />
          <Tooltip content={<TooltipBox fmt={fmt} />} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={CHART_COLORS[0]}
            strokeWidth={1.8}
            dot={{ r: 2 }}
            activeDot={{ r: 5, cursor: onDrillDown ? "pointer" : "default" }}
          />
        </LineChart>
      </ResponsiveContainer>
     </div>
     </div>
    </div>
  );
}

function PieW({ data, fmt, donut = false, onDrillDown }) {
  let rows = _cleanRows(data?.rows);
  if (!rows.length) return <EmptyState />;
  // Top-5 + Others
  if (rows.length > 6) {
    const sorted = [...rows].sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
    const top = sorted.slice(0, 5);
    const rest = sorted.slice(5);
    const othersTotal = rest.reduce((s, r) => s + Number(r.value || 0), 0);
    rows = [...top, { label: "Others", value: othersTotal, _others: rest.length }];
  }
  const total = rows.reduce((s, r) => s + Number(r.value || 0), 0);
  return (
    <div style={{ width: "100%", height: donut ? 280 : 330, minHeight: donut ? 280 : 330 }}>
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 280 }}>
        <PieChart
          onClick={(state) => {
            if (!onDrillDown) return;
            const index = Number(state?.activeTooltipIndex ?? state?.activeIndex);
            const point = Number.isInteger(index) && rows[index]
              ? rows[index]
              : rows.find((row) => String(row.label) === String(state?.activeLabel));
            if (point) onDrillDown(point);
          }}
          style={{ cursor: onDrillDown ? "pointer" : "default" }}
        >
        <Pie
          data={rows}
          dataKey="value"
          nameKey="label"
          innerRadius={donut ? "58%" : 0}
          outerRadius={donut ? "78%" : "68%"}
          stroke="#fff"
          strokeWidth={1.5}
          labelLine={!donut}
          label={
            donut
              ? ({ percent }) =>
                  percent >= 0.07 ? `${(percent * 100).toFixed(0)}%` : ""
              : ({ name, percent }) =>
                  percent >= 0.04
                    ? `${name} · ${(percent * 100).toFixed(1)}%`
                    : ""
          }
          cursor={onDrillDown ? "pointer" : "default"}
        >
          {rows.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip content={<TooltipBox fmt={fmt} total={total} />} />
        <Legend verticalAlign="bottom" iconSize={8} wrapperStyle={{ fontSize: 10.5, lineHeight: "14px" }}
                formatter={(label, entry) => {
                  const r = entry?.payload || rows.find((row) => String(row.label) === String(label));
                  if (!r) return label;
                  const pct = total ? ((Number(r.value || 0) / total) * 100).toFixed(0) + "%" : "";
                  return `${label} · ${compact(r.value)} (${pct})`;
                }} />
        {donut && (
          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 13, fontWeight: 600, fill: "#111827" }}>
            {compact(total)}
          </text>
        )}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function TableW({ data, onTableData, widget, onDrillDown }) {
  const [sortState, setSortState] = useState({
    columnIndex: null,
    direction: "asc",
  });
  const [tableExpanded, setTableExpanded] = useState(false);

  const columns = useMemo(() => data?.columns || [], [data?.columns]);
  const sourceRows = useMemo(() => data?.rows || [], [data?.rows]);

  useEffect(() => {
    if (typeof onTableData === "function") {
      onTableData({ columns, rows: sourceRows });
    }
  }, [columns, sourceRows, onTableData]);

  if (!columns.length) {
    return <EmptyState msg="No columns selected" />;
  }

  if (!sourceRows.length) return <EmptyState />;

  const rows = [...sourceRows];

  if (sortState.columnIndex !== null) {
    const index = sortState.columnIndex;
    const direction = sortState.direction === "asc" ? 1 : -1;

    rows.sort((a, b) => {
      const left = a[index];
      const right = b[index];

      if (left === right) return 0;
      if (left === null || left === undefined) return 1;
      if (right === null || right === undefined) return -1;

      const leftNumber = Number(left);
      const rightNumber = Number(right);

      if (
        left !== "" &&
        right !== "" &&
        Number.isFinite(leftNumber) &&
        Number.isFinite(rightNumber)
      ) {
        return (leftNumber - rightNumber) * direction;
      }

      return String(left).localeCompare(
        String(right),
        undefined,
        { numeric: true, sensitivity: "base" }
      ) * direction;
    });
  }

  const displayedRows = tableExpanded ? rows : rows.slice(0, 5);
  const canExpandTable = rows.length > 5;

  const changeSort = (columnIndex) => {
    setSortState((current) => {
      if (current.columnIndex === columnIndex) {
        return {
          columnIndex,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        columnIndex,
        direction: "asc",
      };
    });
  };

  return (
    <div className="overflow-auto h-full">
      <table className="tbl report-data-table">
        <thead>
          <tr>
            {columns.map((column, index) => {
              const active = sortState.columnIndex === index;

              return (
                <th key={column}>
                  <button
                    type="button"
                    onClick={() => changeSort(index)}
                    className="w-full flex items-center justify-between gap-2 text-left"
                    title={`Sort by ${column}`}
                  >
                    <span>{column}</span>
                    <span className="text-[10px]">
                      {active
                        ? sortState.direction === "asc"
                          ? "▲"
                          : "▼"
                        : "↕"}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {displayedRows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((value, columnIndex) => {
                const isNumber = typeof value === "number";
                const columnName = String(columns[columnIndex] || "");
                const isDateColumn = isReportDateColumn(columnName);
                const processStage =
                  widget?.id === "ceo-production-table"
                    ? columnName === "GradingVarieties"
                      ? "grading"
                      : columnName === "VAVarieties"
                        ? "va"
                        : columnName === "FinishedVarieties"
                          ? "finished"
                          : columnName === "SizeConversionPaths"
                            ? "conversion"
                            : null
                    : null;

                return (
                  <td
                    key={columnIndex}
                    className={isNumber ? "num" : ""}
                  >
                    {processStage ? (
                      <button
                        type="button"
                        onClick={() => onDrillDown?.(processStage, Object.fromEntries(columns.map((column, index) => [column, row[index]])))}
                        className="font-semibold underline underline-offset-2"
                        style={{ color: "#2563eb" }}
                      >
                        View
                      </button>
                    ) : isDateColumn
                      ? formatReportValue(value, columnName)
                    : columnName.toLowerCase().includes("year") && value !== null && value !== undefined && value !== ""
                      ? String(value).replace(/,/g, "")
                      : isNumber
                        ? formatNum(value)
                      : value === null
                        ? "—"
                        : formatReportValue(value, columnName)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {canExpandTable && (
        <div className="flex items-center justify-center py-3">
          <button
            type="button"
            onClick={() => setTableExpanded((current) => !current)}
            className="rounded-lg border px-4 py-2 text-xs font-semibold transition hover:bg-slate-50"
            style={{
              borderColor: "var(--c-border)",
              color: "var(--c-text)",
              background: "#fff",
            }}
          >
            {tableExpanded
              ? "Collapse"
              : `Expand · Show all ${rows.length.toLocaleString()} rows`}
          </button>
        </div>
      )}
    </div>
  );
}

// Module-level cache: key -> { data, ts }. Survives layout shuffles/component remounts within a session.
const WIDGET_CACHE = new Map();
const WIDGET_TTL_MS = 60_000;
const LIVE_KPI_REFRESH_MS = 30_000;
const EMPTY_DASHBOARD_FILTERS = [];

// Coalesce concurrent Buyer/Consignee requests without retaining stale totals.
let salesCustomerCoverageRequest = null;
function fetchSalesCustomerCoverage() {
  if (!salesCustomerCoverageRequest) {
    salesCustomerCoverageRequest = api.get("/ceo/sales-customer-coverage")
      .then(response => response.data)
      .finally(() => { salesCustomerCoverageRequest = null; });
  }
  return salesCustomerCoverageRequest;
}

function widgetCacheKey(widget, filters) {
  const w = widget || {};
  return JSON.stringify({
    id: w.id, type: w.type, ds: w.dataset_id, m: w.measure, a: w.aggregation,
    d: w.dimension, g: w.group_by, l: w.limit, f: w.filters || [], df: filters || [],
    s: w.sort || null,
    displayDimension: w.display_dimension || null,
    buyerSalesFetchLimit: w.orientation === "buyer-sales" ? Math.max(Number(w.limit) || 0, 1000) : null,
    sc: w.selected_columns === undefined ? null : w.selected_columns,
  });
}

export default function WidgetRenderer({
  widget,
  dashboardFilters = EMPTY_DASHBOARD_FILTERS,
  onRefreshKey = 0,
  onDrillDown,
  onTableData,
  showChartCopy = true,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [customerCoverage, setCustomerCoverage] = useState(null);
  const abortRef = useRef(null);
  const chartRef = useRef(null);
  const [copyingChart, setCopyingChart] = useState(false);

  const load = useCallback(async ({ force = false } = {}) => {
    // Direct SQL widgets can provide their own data.
    // Normalize it to the standard widget-data shape expected by charts.
    if (Array.isArray(widget?.data) && widget?.type === "table") {
      const columns = Object.keys(widget.data[0] || {});
      const rows = widget.data.map((row) =>
        columns.map((column) => row?.[column])
      );

      setData({ rows, columns });
      setErr("");
      setLoading(false);
      return;
    }

    if (Array.isArray(widget?.data)) {
      const rows = widget.data
        .map((row) => {
          const rawValue = row?.value ?? row?.[widget.measure];
          const numericValue = Number(rawValue);

          return {
            ...row,
            label: String(row?.label ?? row?.[widget.dimension] ?? "Unknown"),
            value: Number.isFinite(numericValue) ? numericValue : 0,
          };
        })
        .filter((row) => row.label.trim() !== "");

      setData({
        rows,
        columns: Object.keys(widget.data[0] || {}),
      });
      setErr("");
      setLoading(false);
      return;
    }
    // Cancel any in-flight request for this widget
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* noop */ }
    }
    setErr("");
    if (widget.type === "text") {
      setData({ content: widget.text_content || "" }); setLoading(false); return;
    }
    if (!widget.dataset_id) {
      setData(null); setLoading(false); setErr("No dataset configured");
      return;
    }

    const key = widgetCacheKey(widget, dashboardFilters);
    if (!force) {
      const hit = WIDGET_CACHE.get(key);
      if (hit && (Date.now() - hit.ts) < WIDGET_TTL_MS) {
        setData(hit.data); setLoading(false); return;
      }
    }

    if (!force) setLoading(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const queryWidget = widget.orientation === "buyer-sales"
        ? { ...widget, limit: Math.max(Number(widget.limit) || 0, 1000) }
        :
        widget?.type === "treemap" ||
        widget?.type === "contribution-list"
          ? { ...widget, type: "bar" }
          : widget;

      const r = await api.post(
        "/widget/data",
        { widget: queryWidget, filters: dashboardFilters },
        { signal: ctrl.signal, timeout: 35000 }
      );
      if (ctrl.signal.aborted) return;
      WIDGET_CACHE.set(key, { data: r.data, ts: Date.now() });
      setData(r.data);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return; // superseded
      const msg = e?.response?.data?.detail
        || (e?.code === "ECONNABORTED" ? "Query took too long — try adding a filter or smaller limit." : e.message);
      setErr(String(msg));
    } finally {
      if (abortRef.current === ctrl) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  }, [widget, dashboardFilters]);

  const lastRefreshKey = useRef(onRefreshKey);
  useEffect(() => {
    const force = lastRefreshKey.current !== onRefreshKey;
    lastRefreshKey.current = onRefreshKey;
    load({ force });
    return () => { if (abortRef.current) { try { abortRef.current.abort(); } catch (_) { /* noop */ } } }; }, [load, onRefreshKey]);

  // Refresh every dataset-backed view, so charts and tables follow live KPIs.
  useEffect(() => {
    if (!widget?.dataset_id || widget?.type === "text") return undefined;
    const timer = window.setInterval(() => load({ force: true }), LIVE_KPI_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load, widget?.dataset_id, widget?.type]);

  useEffect(() => {
    if (!widget?.dataset_id || widget?.type === "text") return undefined;
    const handleManualRefresh = () => load({ force: true });
    window.addEventListener("camaroo:ceo-refresh-kpis", handleManualRefresh);
    return () => window.removeEventListener("camaroo:ceo-refresh-kpis", handleManualRefresh);
  }, [load, widget?.dataset_id, widget?.type]);

  useEffect(() => {
    const isCustomerContribution =
      widget?.type === "contribution-list" &&
      (widget?.entity_label === "Buyer" || widget?.entity_label === "Consignee");

    if (!isCustomerContribution) {
      setCustomerCoverage(null);
      return;
    }

    let cancelled = false;

    const loadCoverage = async () => {
      try {
        const coverageData = await fetchSalesCustomerCoverage();

        if (cancelled) return;

        setCustomerCoverage(
          widget?.entity_label === "Buyer"
            ? coverageData?.buyers || null
            : coverageData?.consignees || null
        );
      } catch (e) {
        if (!cancelled) {
          // Fail soft here: the contribution widget itself remains usable.
          // It simply falls back to the linked row count.
          setCustomerCoverage(null);
        }
      }
    };

    loadCoverage();
    const timer = window.setInterval(loadCoverage, LIVE_KPI_REFRESH_MS);
    window.addEventListener("camaroo:ceo-refresh-kpis", loadCoverage);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("camaroo:ceo-refresh-kpis", loadCoverage);
    };
  }, [widget?.type, widget?.entity_label, onRefreshKey]);

  if (widget.type === "text") {
    return (
      <div className="p-3 text-[13px] whitespace-pre-wrap" data-testid={`widget-text-${widget.id}`}>
        {widget.text_content || <span style={{ color: "var(--c-text-muted)" }}>Empty text widget</span>}
      </div>
    );
  }

  if (loading) return <WidgetLoadingState />;
  if (err) return <ErrorState msg={String(err)} onRetry={() => load({ force: true })} />;

  const fmt = widget.number_format || {};
  const openDrillDown = (point = {}, row) => {
    if (!onDrillDown) return;

    if (widget?.id === "ceo-production-table" && typeof point === "string" && row) {
      onDrillDown(point, row);
      return;
    }

    onDrillDown({
      widget,
      point,
      data,
      appliedFilters: Array.isArray(dashboardFilters) ? [...dashboardFilters] : [],
    });
  };

  const copyChart = async () => {
    const svg = chartRef.current?.querySelector("svg");
    if (!svg) {
      toast.error("The chart image is not ready to copy.");
      return;
    }
    if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
      toast.error("Copying chart images is not supported by this browser.");
      return;
    }

    setCopyingChart(true);
    try {
      const blob = await chartPngBlob(svg, widget.title, [
        ...(Array.isArray(widget.filters) ? widget.filters : []),
        ...(Array.isArray(dashboardFilters) ? dashboardFilters : []),
      ]);
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      toast.success("Chart copied. Paste it into your document or message.");
    } catch (error) {
      toast.error(error?.message || "Unable to copy the chart image.");
    } finally {
      setCopyingChart(false);
    }
  };

  let renderedWidget;
  switch (widget.type) {
    case "ceo-farm-audit-summary": renderedWidget = <FarmAuditSummary data={data} onDrillDown={openDrillDown} />; break;
    case "kpi":
    case "number":
      renderedWidget = <Kpi
        data={data}
        fmt={fmt}
        title={widget.title}
        onDrillDown={openDrillDown}
        exactOnly={["ceo-va-output-weight", "ceo-soak-out-qty", "ceo-shipment-out-kgs", "ceo-sales-consignee-count", "ceo-dummy-mcs", "ceo-master-mcs"].includes(widget?.id)}
      />;
      break;
    case "bar": renderedWidget = <div>{data?.truncated && Number(widget.limit) >= 10000 ? <div role="status" className="mb-2 text-xs text-amber-700">Showing {data.category_limit} categories. Narrow the filters or open the full detailed report to see the remaining records.</div> : null}<BarW widget={widget} data={data} dimension={widget.dimension} fmt={fmt} buyerSales={widget.orientation === "buyer-sales"} axisLabels={widget.axis_labels} onDrillDown={openDrillDown} /></div>; break;
    case "treemap": renderedWidget = <TreemapW data={data} fmt={fmt} excludeUnassigned={widget.exclude_unassigned === true} entityLabel={widget.entity_label || "Item"} onDrillDown={openDrillDown} />; break;
    case "contribution-list": renderedWidget = <ContributionListW data={data} fmt={fmt} excludeUnassigned={widget.exclude_unassigned === true} entityLabel={widget.entity_label || "Client"} coverage={customerCoverage} includeNonPositive={widget.include_non_positive === true} onDrillDown={openDrillDown} />; break;
    case "line": renderedWidget = <LineW data={data} fmt={fmt} onDrillDown={openDrillDown} widget={widget} />; break;
    case "pie": renderedWidget = <PieW data={data} fmt={fmt} donut={false} onDrillDown={openDrillDown} />; break;
    case "donut": renderedWidget = <PieW data={data} fmt={fmt} donut={true} onDrillDown={openDrillDown} />; break;
    case "table":
      renderedWidget = (
        <TableW
          data={data}
          onTableData={onTableData}
          widget={widget}
          onDrillDown={openDrillDown}
        />
      );
      break;
    default: renderedWidget = <EmptyState msg={`Unsupported widget: ${widget.type}`} />;
  }

  if (data?.truncated && widget.dataset_id === "ds-camaroo-qa-shipment-sales" && !["bar", "table"].includes(widget.type)) {
    renderedWidget = <div><div role="status" className="mb-2 text-xs text-amber-700">Showing {data.category_limit} categories. Narrow the filters or open the full detailed report to see the remaining records.</div>{renderedWidget}</div>;
  }
  if (!["bar", "line", "pie", "donut", "treemap"].includes(widget.type)) {
    return renderedWidget;
  }

  return (
    <div>
      <div className="flex justify-end pb-1">
        <button
          type="button"
          className="btn btn-ghost"
          title="Copy chart image"
          aria-label={`Copy ${widget.title || "chart"} as an image`}
          onClick={copyChart}
          disabled={copyingChart}
          data-testid={`copy-chart-${widget.id}`}
        >
          <Copy size={13} strokeWidth={1.7} />
          <span>{copyingChart ? "Copying…" : "Copy chart"}</span>
        </button>
      </div>
      <div ref={chartRef}>{renderedWidget}</div>
    </div>
  );
}
